import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { FormWrapper } from '@/form-control'
import { CheckboxField, SelectManyField, TextField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IUser } from '@/MODELS/user.model'
import { ISubscription } from '@/MODELS/subscription.model'

import { api, api_delete } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import { AdminHeader } from '@/components/page-layouts/headers/AdminHeader'
import { StripeSubscriptionBadge } from '@/stripe/ManageSubscription'

function AdminUserSubscription({ userId }: { userId: string }) {
    const [subscription, setSubscription] = useState<ISubscription | null>(null)
    const [loading, setLoading] = useState(true)
    const [canceling, setCanceling] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [immediateRevoke, setImmediateRevoke] = useState(false)
    const [revoking, setRevoking] = useState(false)

    const isActive = subscription && ['active', 'trialing', 'past_due'].includes(subscription.subscriptionStatus)
    const isCanceledWithAccess = subscription?.subscriptionStatus === 'canceled'
        && subscription?.benefitsEndDate
        && new Date(subscription.benefitsEndDate) > new Date()

    async function fetchSubscription() {
        setLoading(true)
        try {
            const response = await api<{ subscriptions: ISubscription[] }>(`${ROUTES.admin.user}/${userId}/deletion-preview`)
            if (response.status === 200 && response.data?.subscriptions?.length > 0) {
                setSubscription(response.data.subscriptions[0])
            } else {
                setSubscription(null)
            }
        } catch {
            setSubscription(null)
        } finally {
            setLoading(false)
        }
    }

    async function handleRevokeAccess() {
        setRevoking(true)
        try {
            const response = await api(`${ROUTES.admin.user}/${userId}/revoke-access`, {})
            if (response.status === 200) {
                toast.success('Access revoked immediately')
                await fetchSubscription()
            } else {
                toast.error('Failed to revoke access')
            }
        } catch {
            toast.error('Failed to revoke access')
        } finally {
            setRevoking(false)
        }
    }

    async function handleCancel() {
        setCanceling(true)
        try {
            const response = await api<{ results: { id: string; status: string; error?: string }[] }>(`${ROUTES.admin.user}/${userId}/cancel-subscription`, { immediateRevoke })
            if (response.status === 200) {
                const errors = response.data?.results?.filter(r => r.status === 'error') || []
                if (errors.length > 0) {
                    toast.error(`Cancel failed: ${errors[0].error}`)
                } else {
                    toast.success('Subscription canceled')
                    setShowConfirm(false)
                }
                await fetchSubscription()
            } else {
                toast.error('Failed to cancel subscription')
            }
        } catch {
            toast.error('Failed to cancel subscription')
        } finally {
            setCanceling(false)
        }
    }

    useEffect(() => {
        if (userId && userId !== 'new') fetchSubscription()
        else setLoading(false)
    }, [userId])

    if (loading) return <p className='text-sm text-gray-500'>Loading subscription...</p>
    if (!subscription) return <p className='text-sm text-gray-500'>No subscription found</p>

    const formatDate = (date: Date | string) =>
        new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

    return (
        <div className='space-y-3'>
            <div className='flex items-center gap-3'>
                <StripeSubscriptionBadge subscription={subscription} />
                {subscription.benefitsEndDate && (
                    <span className='text-sm text-gray-600'>
                        Access until {formatDate(subscription.benefitsEndDate)}
                    </span>
                )}
            </div>
            <div className='text-sm text-gray-600'>
                {subscription.createdAt && <span>Started {formatDate(subscription.createdAt)}</span>}
            </div>
            {isActive && !showConfirm && (
                <button
                    onClick={() => setShowConfirm(true)}
                    className='px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors'
                >
                    Cancel Subscription
                </button>
            )}
            {showConfirm && (
                <div className='p-3 border rounded-md bg-amber-50 border-amber-200'>
                    <p className='mb-2 text-sm font-medium text-amber-800'>
                        Are you sure? This will cancel the subscription in Stripe.
                    </p>
                    <label className='flex items-start gap-2 mb-3 cursor-pointer'>
                        <input
                            type='checkbox'
                            checked={immediateRevoke}
                            onChange={(e) => setImmediateRevoke(e.target.checked)}
                            className='mt-0.5 rounded border-gray-300'
                        />
                        <div>
                            <span className='text-sm font-medium text-gray-900'>Immediately revoke access</span>
                            <p className='text-xs text-gray-500'>
                                By default, Stripe cancels the subscription but the user keeps access until
                                their current billing period ends. Check this box to revoke access immediately
                                — the user will see the paywall on their next page load.
                            </p>
                        </div>
                    </label>
                    <div className='flex gap-2'>
                        <button
                            onClick={handleCancel}
                            disabled={canceling}
                            className='px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors'
                        >
                            {canceling ? 'Canceling...' : 'Yes, Cancel'}
                        </button>
                        <button
                            onClick={() => { setShowConfirm(false); setImmediateRevoke(false) }}
                            disabled={canceling}
                            className='px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors'
                        >
                            No, Keep It
                        </button>
                    </div>
                </div>
            )}
            {isCanceledWithAccess && !showConfirm && (
                <div className='p-3 border rounded-md bg-gray-50 border-gray-200'>
                    <p className='mb-2 text-sm text-gray-600'>
                        Subscription is canceled but user still has access until {formatDate(subscription.benefitsEndDate!)}.
                    </p>
                    <button
                        onClick={handleRevokeAccess}
                        disabled={revoking}
                        className='px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 transition-colors'
                    >
                        {revoking ? 'Revoking...' : 'Revoke Access Now'}
                    </button>
                </div>
            )}
        </div>
    )
}

// Format any phone string as ###-###-#### for display in the admin form.
// Strips non-digits, drops a leading "1" only when there are 11 digits total
// (so a 10-digit number starting with 1 still renders correctly), and pads
// to whatever the user has typed so far.
function formatPhoneDisplay(value: string | undefined | null): string {
    if (!value) return ''
    let digits = String(value).replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
    digits = digits.slice(0, 10)
    if (digits.length === 0) return ''
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

function PhoneNumberField({
    value,
    onChange,
}: {
    value: string | undefined
    onChange: (next: string) => void
}) {
    const display = formatPhoneDisplay(value)
    const isValid = !value || /^\d{3}-\d{3}-\d{4}$/.test(display)
    return (
        <div className='field-container'>
            <label className='field-label'>Phone Number</label>
            <input
                type='tel'
                inputMode='numeric'
                autoComplete='tel-national'
                placeholder='555-123-4567'
                value={display}
                onChange={(e) => onChange(formatPhoneDisplay(e.target.value))}
                maxLength={12}
                className={`field-input${isValid ? '' : ' ring-amber-600 focus-within:ring-amber-600'}`}
            />
            <p className='mt-1 text-xs text-gray-500'>
                US 10-digit number, format 555-123-4567. Leave blank to disable SMS verification.
            </p>
            {!isValid && (
                <p className='mt-1 text-xs text-amber-700'>
                    Phone number must be in the format 555-123-4567.
                </p>
            )}
        </div>
    )
}

export default function AdminUserForm(props: { id?: string }) {
    const navigate = useNavigate()
    const params = useParams()
    const id = props?.id ?? params?.id ?? 'new'
    const { user: currentUser, fetchUser } = useAuth()

    return (
        <main>
            <AdminPageHeader
                heading={!id ? 'Create New User' : 'Edit User'}
                subheading='Add new users or edit existing profiles'
                className='py-5'
            />
            <FormWrapper<IUser>
                endpoint={ROUTES.admin.user}
                id={id}
                displayAs='standalone-card'
                redirectAfterSubmit
                validationRules={[
                    {
                        field: 'phoneNumber',
                        // Accept either the canonical ###-###-#### display format OR the
                        // E.164 (+1XXXXXXXXXX) form already stored on existing users from
                        // the mongoose setter. PhoneNumberField only writes back to
                        // formValues on user input, so a user who never touches the field
                        // submits the raw stored value — strict ###-###-#### would
                        // reject those legacy records. The backend setter normalizes either
                        // shape on save.
                        isValid: (v) => {
                            if (!v) return true
                            const digits = String(v).replace(/\D/g, '')
                            return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
                        },
                        prompt: 'Phone number must be a US 10-digit number',
                    },
                ]}
                callbackAfterSubmit={() => {
                    // If editing own user, refresh auth context so test user settings take effect immediately
                    if (id === currentUser?._id) fetchUser()
                }}
                additionalSubmissionRowContent={
                    <div className='flex items-center justify-between w-full gap-x-2 h-fit'>
                        <Button.Back />
                        <Button.ConfirmedDelete
                            onConfirmDelete={async () => {
                                await api_delete(ROUTES.admin.user + '/' + id)
                                toast.success('User deleted')
                                navigate(-1)
                            }}
                        />
                    </div>
                }
            >
                {(f, { formValues, setFormValues }) => (
                    <div className='space-y-10'>
                        <section className='space-y-5'>
                            <h1 className='text-3xl font-semibold'>Basic Information</h1>
                            <div className='grid md:grid-cols-2 gap-x-5'>
                                <TextField {...f('name')} label='Name' />
                                <TextField {...f('email')} label='Email' />
                            </div>
                        </section>
                        <section className='space-y-5'>
                            <h1 className='text-3xl font-semibold'>Permissions & Settings</h1>
                            <div className='grid md:grid-cols-2 gap-x-5'>
                                <CheckboxField
                                    {...f('onboardingComplete')}
                                    label='Onboarding Complete'
                                    display='inline'
                                />
                                <CheckboxField {...f('permissions.isAdmin')} label='Site Admin' display='inline' />
                            </div>
                        </section>
                        <section className='space-y-5'>
                            <h1 className='text-3xl font-semibold'>Security</h1>
                            <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                                <p className='text-sm text-blue-700'>
                                    <strong>SMS Verification.</strong> When a phone number is set for an admin user,
                                    they will be required to enter a verification code sent via SMS on every login.
                                    By saving a phone number, you consent to receiving SMS verification codes at that number.
                                    Standard message and data rates may apply. Remove the phone number at any time to stop receiving codes.
                                </p>
                            </div>
                            <PhoneNumberField
                                value={(formValues as any)?.phoneNumber}
                                onChange={(next) => setFormValues({ ...formValues, phoneNumber: next })}
                            />
                        </section>
                        <section className='space-y-5'>
                            <h1 className='text-3xl font-semibold'>Test User Settings</h1>
                            <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                                <p className='text-sm text-blue-700'>
                                    <strong>Per-user overrides.</strong> These settings override the app-wide Paywall and Stripe Mode
                                    settings (configured in App Settings) for this specific user only. Other users are not affected.
                                </p>
                            </div>
                            <CheckboxField
                                value={(formValues as any)?.testUser?.enabled ?? false}
                                onChange={(value: boolean) => {
                                    if (value) {
                                        setFormValues({
                                            ...formValues,
                                            testUser: {
                                                ...(formValues as any)?.testUser,
                                                enabled: true,
                                            }
                                        })
                                    } else {
                                        setFormValues({
                                            ...formValues,
                                            testUser: { ...(formValues as any)?.testUser, enabled: false }
                                        })
                                    }
                                }}
                                label='Mark as Test User'
                                display='inline'
                            />
                            {(formValues as any)?.testUser?.enabled && (
                                <div className='space-y-5 pl-4 border-l-2 border-blue-200'>
                                    <p className='text-xs text-gray-500'>
                                        Test users are automatically excluded from the admin analytics dashboard.
                                    </p>
                                    <div className='field-container'>
                                        <label className='field-label'>Paywall & Stripe Mode</label>
                                        <select
                                            value={(formValues as any)?.testUser?.paywallMode ?? ''}
                                            onChange={(e) => {
                                                setFormValues({
                                                    ...formValues,
                                                    testUser: {
                                                        ...(formValues as any)?.testUser,
                                                        paywallMode: e.target.value,
                                                    }
                                                })
                                            }}
                                            className='field-input'
                                        >
                                            <option value='' disabled>Select paywall mode...</option>
                                            <option value='stripe_test'>Stripe Test Mode — paywall shown, no real charges</option>
                                            <option value='stripe_live'>Stripe Live Mode — paywall shown, real charges</option>
                                            <option value='off'>Off — full access, no paywall</option>
                                        </select>
                                    </div>
                                    {(formValues as any)?.testUser?.paywallMode === 'off' && (
                                        <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
                                            <p className='text-sm text-green-700'>
                                                This user will have full access with no paywall, regardless of the app-wide settings.
                                            </p>
                                        </div>
                                    )}
                                    {(formValues as any)?.testUser?.paywallMode === 'stripe_live' && (
                                        <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                                            <p className='text-sm text-red-700 font-medium'>
                                                Warning: Real Stripe charges will be processed for this user, even if the app-wide
                                                Stripe mode is set to Test.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                        {id !== 'new' && (
                            <section className='space-y-5'>
                                <h1 className='text-3xl font-semibold'>Subscription</h1>
                                <AdminUserSubscription userId={id} />
                            </section>
                        )}
                    </div>
                )}
            </FormWrapper>
        </main>
    )
}
